import { computed, inject, Injectable } from '@angular/core';
import { OrderStepperFlowProgress, OrderStepperProgress } from '../../../../../../models/order/workflow/order-stepper.model';
import { OrderStateServiceV2 } from '../../order-state.service';

const EMPTY_FLOW_PROGRESS: OrderStepperFlowProgress = { visited: [], completed: [] };

/**
 * Owns OrderV2.stepperProgress. It is a root field read straight from the order signal (like currentRoute),
 * so it is restored with the draft and needs no separate bridge hydration.
 */
@Injectable({ providedIn: 'root' })
export class OrderStepperStateService {
    private orderStateService = inject(OrderStateServiceV2);

    readonly progress = computed<OrderStepperProgress>(() =>
        this.orderStateService.order()?.stepperProgress ?? { flows: {} });

    readonly locked = computed(() => !!this.progress().locked);

    flowProgress(flow: string | null | undefined): OrderStepperFlowProgress {
        return (flow && this.progress().flows[flow]) || EMPTY_FLOW_PROGRESS;
    }

    markVisited(flow: string, routes: string[]): void {
        this.updateFlow(flow, current => ({ ...current, visited: this.merge(current.visited, routes) }));
    }

    markCompleted(flow: string, routes: string[]): void {
        this.updateFlow(flow, current => ({
            visited: this.merge(current.visited, routes),
            completed: this.merge(current.completed, routes)
        }));
    }

    lock(): void {
        if (this.locked() || !this.orderStateService.order()) return;
        this.orderStateService.updateOrder({ stepperProgress: { ...this.progress(), locked: true } });
    }

    private updateFlow(flow: string, update: (current: OrderStepperFlowProgress) => OrderStepperFlowProgress): void {
        if (!flow || !this.orderStateService.order()) return;
        const current = this.flowProgress(flow);
        const next = update(current);
        if (next.visited.length === current.visited.length && next.completed.length === current.completed.length) {
            return;
        }
        const progress = this.progress();
        this.orderStateService.updateOrder({
            stepperProgress: { ...progress, flows: { ...progress.flows, [flow]: next } }
        });
    }

    private merge(existing: string[], routes: string[]): string[] {
        const added = routes.filter(route => !!route && !existing.includes(route));
        return added.length ? [...existing, ...added] : existing;
    }
}
